import { ref } from 'vue'

const MAX_RECORDING_TIME_MIN = 30
const MAX_RECORDING_TIME_MS = MAX_RECORDING_TIME_MIN * 60 * 1000
const AUDIO_BITRATE = 256000
const AUDIO_SAMPLE_RATE = 48000

const SUPPORTED_MIME_TYPES = {
  'audio/webm;codecs=opus': 'webm',
  'audio/ogg;codecs=opus': 'ogg',
  'audio/webm': 'webm',
  'audio/mp4': 'mp4',
}

const pad = (num: number): string => num.toString().padStart(2, '0')

export function formatSeconds(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainingSeconds = seconds % 60

  return hours > 0
    ? `${pad(hours)}:${pad(minutes)}:${pad(Math.floor(remainingSeconds))}`
    : `${pad(minutes)}:${pad(Math.floor(seconds))}`
}

export default () => {
  const audioBlobs = ref<Array<Blob>>([])
  const mediaRecorder = ref<MediaRecorder>()
  const streamBeingCaptured = ref<MediaStream>()
  const elapsedTime = ref(0)
  const isRecording = ref(false)
  const audio = document.createElement('audio')
  audio.muted = true

  audio.addEventListener('timeupdate', () => {
    elapsedTime.value = audio.currentTime

    if (audio.currentTime > MAX_RECORDING_TIME_MS) {
      stop()
    }
  })

  const getSupportedMimeType = () => {
    const supported = Object.keys(SUPPORTED_MIME_TYPES).find(mimeType =>
      MediaRecorder.isTypeSupported(mimeType)
    )
    if (!supported) {
      throw new Error('None of the preferred audio MIME types are supported')
    }
    return supported as keyof typeof SUPPORTED_MIME_TYPES
  }

  const getSupportedExtension = (mimeType: keyof typeof SUPPORTED_MIME_TYPES) =>
    SUPPORTED_MIME_TYPES[mimeType] ?? 'ogg'

  const recorderOptions = {
    mimeType: '',
    extension: ''
  }

  function onRecording() {
    audio.play()
    isRecording.value = true
  }

  function onPauseRecording() {
    audio.pause()
    isRecording.value = false
  }

  function start() {
    if (!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) {
      return Promise.reject(
        new Error('Media devices is not supported in this browser.')
      )
    }

    if (mediaRecorder.value?.state === 'paused') {
      mediaRecorder.value.resume()
      return Promise.resolve()
    }

    return navigator.mediaDevices.getUserMedia({
      audio: {
        sampleRate: { ideal: AUDIO_SAMPLE_RATE },
        echoCancellation: { ideal: true },
        noiseSuppression: { ideal: true },
        autoGainControl: { ideal: true },
      }
    })
      .then((stream: MediaStream) => {
        const mimeType = getSupportedMimeType()
        recorderOptions.mimeType = mimeType
        recorderOptions.extension = getSupportedExtension(mimeType)

        if (!MediaRecorder.isTypeSupported(recorderOptions.mimeType)) {
          throw new Error(`The MIME type ${recorderOptions.mimeType} is not supported on this browser`)
        }

        audio.srcObject = stream
        audioBlobs.value = []
        mediaRecorder.value = new MediaRecorder(stream, {
          mimeType: recorderOptions.mimeType,
          audioBitsPerSecond: AUDIO_BITRATE,
        })
        streamBeingCaptured.value = stream

        mediaRecorder.value.addEventListener('dataavailable', (event) => {
          audioBlobs.value.push(event.data)
        })

        mediaRecorder.value.addEventListener('start', onRecording)
        mediaRecorder.value.addEventListener('resume', onRecording)
        mediaRecorder.value.addEventListener('pause', onPauseRecording)
        mediaRecorder.value.addEventListener('stop', onPauseRecording)
        mediaRecorder.value.start()
      })
  }

  function pause() {
    mediaRecorder.value?.pause()
  }

  function stop(): Promise<{ blob: Blob, duration: number }> {
    return new Promise((resolve, reject) => {
      if (!mediaRecorder.value) {
        return reject(new Error('No MediaRecorder'))
      }

      const mimeType = mediaRecorder.value.mimeType
      const duration = elapsedTime.value

      mediaRecorder.value.addEventListener('stop', () => {
        const blob = new Blob(audioBlobs.value, { type: mimeType })
        resolve({ blob, duration })
      })

      cancel()
    })
  }

  function cancel() {
    if (!mediaRecorder.value) {
      return
    }
    mediaRecorder.value.stop()
    stopStream()
    resetRecording()
  }

  function stopStream() {
    if (!streamBeingCaptured.value) {
      return
    }
    streamBeingCaptured.value.getTracks()
      .forEach(track => track.stop())
  }

  function resetRecording() {
    mediaRecorder.value = undefined
    streamBeingCaptured.value = undefined
    audioBlobs.value = []
    elapsedTime.value = 0
    audio.srcObject = null
  }

  return {
    audioBlobs,
    mediaRecorder,
    streamBeingCaptured,
    recorderOptions,
    start,
    pause,
    stop,
    cancel,
    formatSeconds,
    resetRecording,
    isRecording,
    elapsedTime,
    MAX_RECORDING_TIME_MS,
  }
}
